// Offline Store using IndexedDB via idb library
// Provides offline data persistence for PWA functionality

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Device, DevicePosition } from './devices.store';

// Geofence interface for offline storage
export interface OfflineGeofence {
  id: string;
  name: string;
  type: 'CIRCLE' | 'POLYGON' | 'CORRIDOR';
  color: string;
  centerLatitude: number | null;
  centerLongitude: number | null;
  radius: number | null;
  coordinates: [number, number][] | null;
  corridorWidth: number | null;
  category: string | null;
  isActive: boolean;
  createdAt: string;
}

// Sync queue item for background sync
export interface SyncQueueItem {
  id: string;
  type: 'ACKNOWLEDGE_ALERT' | 'UPDATE_DEVICE' | 'CREATE_GEOFENCE' | 'UPDATE_GEOFENCE' | 'DELETE_GEOFENCE';
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  createdAt: string;
  retryCount: number;
  lastError?: string;
}

// Position history entry
export interface PositionHistoryEntry {
  id: string; // deviceId_timestamp
  deviceId: string;
  position: DevicePosition;
  savedAt: string;
}

// IndexedDB Schema
interface TrackFleetDB extends DBSchema {
  devices: {
    key: string;
    value: Device & { savedAt: string };
    indexes: { 'by-imei': string; 'by-savedAt': string };
  };
  positions: {
    key: string;
    value: PositionHistoryEntry;
    indexes: { 'by-deviceId': string; 'by-savedAt': string };
  };
  geofences: {
    key: string;
    value: OfflineGeofence & { savedAt: string };
    indexes: { 'by-savedAt': string };
  };
  syncQueue: {
    key: string;
    value: SyncQueueItem;
    indexes: { 'by-createdAt': string; 'by-type': string };
  };
  metadata: {
    key: string;
    value: { key: string; value: unknown; updatedAt: string };
  };
}

const DB_NAME = 'trackfleet-offline';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<TrackFleetDB>> | null = null;

// Initialize the database
async function getDB(): Promise<IDBPDatabase<TrackFleetDB>> {
  if (!dbPromise) {
    dbPromise = openDB<TrackFleetDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Devices store
        if (!db.objectStoreNames.contains('devices')) {
          const devicesStore = db.createObjectStore('devices', { keyPath: 'id' });
          devicesStore.createIndex('by-imei', 'imei');
          devicesStore.createIndex('by-savedAt', 'savedAt');
        }

        // Positions store (last known positions)
        if (!db.objectStoreNames.contains('positions')) {
          const positionsStore = db.createObjectStore('positions', { keyPath: 'id' });
          positionsStore.createIndex('by-deviceId', 'deviceId');
          positionsStore.createIndex('by-savedAt', 'savedAt');
        }

        // Geofences store
        if (!db.objectStoreNames.contains('geofences')) {
          const geofencesStore = db.createObjectStore('geofences', { keyPath: 'id' });
          geofencesStore.createIndex('by-savedAt', 'savedAt');
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
          syncStore.createIndex('by-createdAt', 'createdAt');
          syncStore.createIndex('by-type', 'type');
        }

        // Metadata store (for last sync time, etc.)
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

// ============================================
// Devices Operations
// ============================================

export async function saveDevices(devices: Device[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('devices', 'readwrite');
  const savedAt = new Date().toISOString();

  await Promise.all([
    ...devices.map((device) => tx.store.put({ ...device, savedAt })),
    tx.done,
  ]);

  // Update last sync metadata
  await setMetadata('lastDevicesSync', savedAt);
}

export async function getDevices(): Promise<Device[]> {
  const db = await getDB();
  const devices = await db.getAll('devices');
  return devices.map(({ savedAt: _, ...device }) => device as Device);
}

export async function getDevice(id: string): Promise<Device | undefined> {
  const db = await getDB();
  const device = await db.get('devices', id);
  if (device) {
    const { savedAt: _, ...rest } = device;
    return rest as Device;
  }
  return undefined;
}

export async function clearDevices(): Promise<void> {
  const db = await getDB();
  await db.clear('devices');
}

// ============================================
// Positions Operations
// ============================================

export async function savePosition(deviceId: string, position: DevicePosition): Promise<void> {
  const db = await getDB();
  const savedAt = new Date().toISOString();
  const id = `${deviceId}_${position.timestamp}`;

  await db.put('positions', {
    id,
    deviceId,
    position,
    savedAt,
  });
}

export async function savePositions(positions: Array<{ deviceId: string; position: DevicePosition }>): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('positions', 'readwrite');
  const savedAt = new Date().toISOString();

  await Promise.all([
    ...positions.map(({ deviceId, position }) =>
      tx.store.put({
        id: `${deviceId}_${position.timestamp}`,
        deviceId,
        position,
        savedAt,
      })
    ),
    tx.done,
  ]);
}

export async function getLastPosition(deviceId: string): Promise<DevicePosition | undefined> {
  const db = await getDB();
  const positions = await db.getAllFromIndex('positions', 'by-deviceId', deviceId);
  if (positions.length === 0) return undefined;

  // Sort by timestamp and return the most recent
  positions.sort((a, b) =>
    new Date(b.position.timestamp).getTime() - new Date(a.position.timestamp).getTime()
  );
  return positions[0].position;
}

export async function getPositionHistory(deviceId: string, limit = 100): Promise<DevicePosition[]> {
  const db = await getDB();
  const positions = await db.getAllFromIndex('positions', 'by-deviceId', deviceId);

  // Sort by timestamp descending and limit
  positions.sort((a, b) =>
    new Date(b.position.timestamp).getTime() - new Date(a.position.timestamp).getTime()
  );

  return positions.slice(0, limit).map((p) => p.position);
}

export async function clearOldPositions(maxAgeHours = 24): Promise<number> {
  const db = await getDB();
  const cutoff = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000).toISOString();

  const tx = db.transaction('positions', 'readwrite');
  const positions = await tx.store.getAll();
  let deletedCount = 0;

  for (const pos of positions) {
    if (pos.savedAt < cutoff) {
      await tx.store.delete(pos.id);
      deletedCount++;
    }
  }

  await tx.done;
  return deletedCount;
}

// ============================================
// Geofences Operations
// ============================================

export async function saveGeofences(geofences: OfflineGeofence[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('geofences', 'readwrite');
  const savedAt = new Date().toISOString();

  await Promise.all([
    ...geofences.map((geofence) => tx.store.put({ ...geofence, savedAt })),
    tx.done,
  ]);

  await setMetadata('lastGeofencesSync', savedAt);
}

export async function getGeofences(): Promise<OfflineGeofence[]> {
  const db = await getDB();
  const geofences = await db.getAll('geofences');
  return geofences.map(({ savedAt: _, ...geofence }) => geofence as OfflineGeofence);
}

export async function clearGeofences(): Promise<void> {
  const db = await getDB();
  await db.clear('geofences');
}

// ============================================
// Sync Queue Operations
// ============================================

export async function addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'retryCount'>): Promise<string> {
  const db = await getDB();
  const id = `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  await db.put('syncQueue', {
    ...item,
    id,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });

  return id;
}

export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  const db = await getDB();
  const items = await db.getAllFromIndex('syncQueue', 'by-createdAt');
  return items;
}

export async function getSyncQueueItem(id: string): Promise<SyncQueueItem | undefined> {
  const db = await getDB();
  return db.get('syncQueue', id);
}

export async function updateSyncQueueItem(id: string, updates: Partial<SyncQueueItem>): Promise<void> {
  const db = await getDB();
  const item = await db.get('syncQueue', id);
  if (item) {
    await db.put('syncQueue', { ...item, ...updates });
  }
}

export async function removeSyncQueueItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('syncQueue', id);
}

export async function clearSyncQueue(): Promise<void> {
  const db = await getDB();
  await db.clear('syncQueue');
}

export async function getSyncQueueCount(): Promise<number> {
  const db = await getDB();
  return db.count('syncQueue');
}

// ============================================
// Metadata Operations
// ============================================

export async function setMetadata(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('metadata', {
    key,
    value,
    updatedAt: new Date().toISOString(),
  });
}

export async function getMetadata<T = unknown>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const item = await db.get('metadata', key);
  return item?.value as T | undefined;
}

export async function getLastSyncTime(type: 'devices' | 'geofences' | 'positions'): Promise<Date | null> {
  const key = `last${type.charAt(0).toUpperCase() + type.slice(1)}Sync`;
  const value = await getMetadata<string>(key);
  return value ? new Date(value) : null;
}

// ============================================
// Utility Functions
// ============================================

export async function clearAllOfflineData(): Promise<void> {
  const db = await getDB();
  await Promise.all([
    db.clear('devices'),
    db.clear('positions'),
    db.clear('geofences'),
    db.clear('syncQueue'),
    db.clear('metadata'),
  ]);
}

export async function getOfflineDataStats(): Promise<{
  devicesCount: number;
  positionsCount: number;
  geofencesCount: number;
  syncQueueCount: number;
  lastDevicesSync: Date | null;
  lastGeofencesSync: Date | null;
}> {
  const db = await getDB();

  const [devicesCount, positionsCount, geofencesCount, syncQueueCount] = await Promise.all([
    db.count('devices'),
    db.count('positions'),
    db.count('geofences'),
    db.count('syncQueue'),
  ]);

  const [lastDevicesSync, lastGeofencesSync] = await Promise.all([
    getLastSyncTime('devices'),
    getLastSyncTime('geofences'),
  ]);

  return {
    devicesCount,
    positionsCount,
    geofencesCount,
    syncQueueCount,
    lastDevicesSync,
    lastGeofencesSync,
  };
}

// Export a simple object for commonly used operations
export const offlineStore = {
  // Devices
  saveDevices,
  getDevices,
  getDevice,
  clearDevices,

  // Positions
  savePosition,
  savePositions,
  getLastPosition,
  getPositionHistory,
  clearOldPositions,

  // Geofences
  saveGeofences,
  getGeofences,
  clearGeofences,

  // Sync Queue
  addToSyncQueue,
  getSyncQueue,
  getSyncQueueItem,
  updateSyncQueueItem,
  removeSyncQueueItem,
  clearSyncQueue,
  getSyncQueueCount,

  // Metadata
  setMetadata,
  getMetadata,
  getLastSyncTime,

  // Utilities
  clearAllOfflineData,
  getOfflineDataStats,
};

export default offlineStore;
